import {
  computed,
  type ComputedRef,
  type MaybeRefOrGetter,
  toValue,
} from 'vue'
import type { DeploymentDefinition } from '~domain/algorithm/jobDefinitions/DeploymentDefinition'
import type { JobBlobDependency } from '~domain/algorithm/jobDefinitions/dependencies/JobBlobDependency'
import type { JobParameterDependency } from '~domain/algorithm/jobDefinitions/dependencies/JobParameterDependency'
import type { DriftDetection } from '~domain/driftDetection/DriftDetection'
import { useDataDriftEntitlement } from '~stores/composables/useEntitlements'
import { useAlgorithmsStore } from '~stores/useAlgorithmsStore'
import { useJobsStore } from '~stores/useJobsStore'


export const RV_DRIFT_DETECTOR_BLOB_DEFINITION_ID =
  'ai.robovision.blobs.drift-detectors.RvDriftDetector'

const RV_DRIFT_DETECTOR_INFERENCE_PARAMETERS_DEFINITION_ID_SUFFIX =
  'RvDriftDetectionInferenceParameters'

export function isDriftDetectionInferenceParameterDependency (
  dep: JobParameterDependency,
): boolean {
  return dep.definitionId.endsWith(RV_DRIFT_DETECTOR_INFERENCE_PARAMETERS_DEFINITION_ID_SUFFIX)
}

export function findDriftDetectionInferenceParametersDependency (
  parametersDependencies: Record<string, JobParameterDependency>,
): JobParameterDependency | undefined {
  return Object.values(parametersDependencies).find(isDriftDetectionInferenceParameterDependency)
}

export function findDriftDetectorBlobDependency (
  blobDependencies: Record<string, JobBlobDependency>,
): JobBlobDependency | undefined {
  return Object.values(blobDependencies).find(
    d => d.definitionId === RV_DRIFT_DETECTOR_BLOB_DEFINITION_ID,
  )
}

export function useDriftDetectionAvailable (
  deploymentDefinition: MaybeRefOrGetter<DeploymentDefinition | undefined>,
): ComputedRef<boolean> {
  const { isGranted: isDataDriftGranted } = useDataDriftEntitlement()

  return computed(() => isDataDriftGranted.value &&
    !!findDriftDetectorBlobDependency(toValue(deploymentDefinition)?.blobDependencies ?? {}))
}

export function useDriftDetectorParams () {
  const {
    getAlgorithmDefinition,
    loadAlgorithmDefinition,
  } = useAlgorithmsStore()
  const {
    getJobArtifacts,
    loadJobArtifacts,
  } = useJobsStore()

  async function resolveBlobResourceId (job: DriftDetection): Promise<string | undefined> {
    const algorithmDefinition = getAlgorithmDefinition(job.algorithmId, job.algorithmVersion) ??
      await loadAlgorithmDefinition({
        id: job.algorithmId,
        version: job.algorithmVersion,
      })

    const jobDefinition = algorithmDefinition?.getJobDefinitionById(job.jobDefinitionId)
    if (!jobDefinition) return undefined

    const artifactId = Object.values(jobDefinition.blobArtifacts).find(
      a => a.definitionId === RV_DRIFT_DETECTOR_BLOB_DEFINITION_ID,
    )?.artifactId
    if (!artifactId) return undefined

    await loadJobArtifacts(job.id, true)

    return getJobArtifacts(job.id)?.blobs?.[artifactId]?.[0]
  }

  return { resolveBlobResourceId }
}
